(() => {
  const selector = '[data-lmb-quantity-discount]';
  const cartSelector = '[data-lmb-cart-discount]';
  if (window.__lmbPdpDiscounts) {
    window.__lmbPdpDiscounts.init(document);
    return;
  }

  const instances = new Map();

  let cartManager = null;
  const initialiseCart = (root) => {
    if (cartManager) return;
    let eligibleIds;
    try { eligibleIds = JSON.parse(root.querySelector('[data-lmb-discount-eligible-products]')?.textContent || '[]').map(String); } catch { return; }
    const ownedCodes = (root.dataset.lmbDiscountCodes || '').split(',').map((code) => code.trim()).filter(Boolean);
    if (!eligibleIds.length || ownedCodes.length !== 3) return;
    let currentCart = null;
    let task = null;
    let again = false;
    let checkoutPending = false;
    let pendingQuantity = false;
    let cartVersion = 0;
    let quantityTimer;
    let quantityWaiters = [];
    const replay = new WeakSet();
    const paymentStates = new WeakMap();
    const payments = (busy) => {
      document.querySelectorAll('.js-cart-footer-additional-buttons').forEach((element) => {
        if (!paymentStates.has(element)) paymentStates.set(element, element.hidden);
        element.hidden = paymentStates.get(element) || busy;
        element.toggleAttribute('data-lmb-cart-payment-pending', busy);
      });
    };
    const report = (ready) => {
      payments(!ready);
      document.querySelectorAll(cartSelector).forEach((element) => { element.dataset.lmbDiscountReady = String(ready); });
      document.querySelectorAll('[data-lmb-cart-discount-error]').forEach((element) => { element.hidden = ready; });
    };
    const waitForQuantity = () => pendingQuantity ? new Promise((resolve) => quantityWaiters.push(resolve)) : Promise.resolve(true);
    const sync = () => {
      if (task) { again = true; return task; }
      payments(true);
      const route = window.Shopify?.routes?.root || '/';
      const endpoint = (route.endsWith('/') ? route : route + '/') + 'cart';
      task = (async () => {
        do {
          again = false;
          if (!await waitForQuantity()) throw new Error('quantity');
          const version = cartVersion;
          const response = await fetch(endpoint + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('cart');
          const cart = await response.json();
          if (version !== cartVersion || pendingQuantity) { again = true; continue; }
          if (!Array.isArray(cart.items) || !Array.isArray(cart.discount_codes)) throw new Error('cart');
          const quantity = cart.items.reduce((total, item) => eligibleIds.includes(String(item.product_id)) ? total + Number(item.quantity || 0) : total, 0);
          const index = quantity >= 8 ? 2 : quantity >= 4 ? 1 : quantity >= 2 ? 0 : -1;
          const current = cart.discount_codes.map((entry) => entry.code).filter((code) => typeof code === 'string' && code.length);
          const external = current.filter((code) => !ownedCodes.some((owned) => owned.toUpperCase() === code.toUpperCase()));
          const expected = Array.from(new Set([...external, ...(index >= 0 ? [ownedCodes[index]] : [])].map((code) => code.toUpperCase())));
          const currentNormalized = current.map((code) => code.toUpperCase());
          let resolvedCart = cart;
          if (expected.length !== currentNormalized.length || expected.some((code) => !currentNormalized.includes(code))) {
            const sectionIds = Array.from(document.querySelectorAll('[data-section-type="cart-template"]')).map((element) => element.dataset.sectionId).filter(Boolean).slice(0, 5);
            const update = await fetch(endpoint + '/update.js', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: expected.join(','), ...(sectionIds.length ? { sections: Array.from(new Set(sectionIds)).join(',') } : {}) }) });
            if (!update.ok) throw new Error('discount');
            resolvedCart = await update.json();
            if (version !== cartVersion || pendingQuantity) { again = true; continue; }
            if (!Array.isArray(resolvedCart.items) || !Array.isArray(resolvedCart.discount_codes) || expected.some((code) => !resolvedCart.discount_codes.some((entry) => String(entry.code).toUpperCase() === code))) throw new Error('discount');
            if (window.themeCore) {
              window.themeCore.cartObject = resolvedCart;
              if (resolvedCart.sections) window.themeCore.EventBus?.emit('cart:refresh', resolvedCart.sections);
            }
          }
          currentCart = resolvedCart;
        } while (again || pendingQuantity);
        report(true);
        instances.forEach((instance) => instance.updateCart && instance.updateCart(currentCart, true));
        return true;
      })().catch(() => {
        report(false);
        instances.forEach((instance) => instance.updateCart && instance.updateCart(currentCart, false));
        return false;
      }).finally(() => { task = null; });
      return task;
    };
    const markQuantity = (event) => {
      if (!event.target.closest('[data-cart-item]')) return;
      if (event.type === 'click' && !event.target.closest('[data-quantity-increase], [data-quantity-decrease], .js-cart-item-remove')) return;
      pendingQuantity = true;
      cartVersion += 1;
      payments(true);
      clearTimeout(quantityTimer);
      quantityTimer = setTimeout(() => {
        pendingQuantity = false;
        quantityWaiters.splice(0).forEach((resolve) => resolve(false));
      }, 5000);
    };
    const updated = () => {
      cartVersion += 1;
      pendingQuantity = false;
      clearTimeout(quantityTimer);
      quantityWaiters.splice(0).forEach((resolve) => resolve(true));
      sync();
    };
    const ready = () => sync();
    const click = async (event) => {
      if (event.defaultPrevented || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const target = event.target.closest('a[href], button[name="checkout"], input[name="checkout"]');
      if (!target || replay.has(target)) return;
      if (target.tagName === 'A') {
        let url;
        try { url = new URL(target.href, window.location.href); } catch { return; }
        if (url.origin !== window.location.origin || !/\/checkout(?:\/|$)/.test(url.pathname)) return;
      }
      event.preventDefault(); event.stopImmediatePropagation();
      if (checkoutPending) return;
      checkoutPending = true; target.setAttribute('aria-busy', 'true');
      const success = await ready();
      checkoutPending = false; target.removeAttribute('aria-busy');
      if (success) { replay.add(target); target.click(); replay.delete(target); }
    };
    const submit = async (event) => {
      if (event.defaultPrevented) return;
      const target = event.target, submitter = event.submitter;
      if (!target || target.tagName !== 'FORM' || replay.has(target)) return;
      if (!(submitter && submitter.name === 'checkout') && !/\/checkout(?:\/|$)/.test(target.getAttribute('action') || '')) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (checkoutPending) return;
      checkoutPending = true;
      const success = await ready();
      checkoutPending = false;
      if (success) { replay.add(target); target.requestSubmit(submitter || undefined); replay.delete(target); }
    };
    let bus = null;
    const attach = () => {
      if (!bus && window.themeCore?.EventBus) { bus = window.themeCore.EventBus; bus.listen('cart:updated', updated); }
      sync();
    };
    document.addEventListener('click', markQuantity, true);
    document.addEventListener('change', markQuantity, true);
    document.addEventListener('click', click, true);
    document.addEventListener('submit', submit, true);
    document.addEventListener('theme:all:loaded', attach, { once: true });
    window.addEventListener('load', attach, { once: true });
    cartManager = { sync, getCart: () => currentCart };
    attach();
  };

  const initialise = (root) => {
    if (instances.has(root)) return;
    const form = root.closest('[data-js-product-form]');
    const input = form && form.querySelector('[name="quantity"][data-quantity-input]');
    const variantInput = form && form.querySelector('[name="id"]');
    const data = root.querySelector('[data-lmb-discount-variants]');
    if (!form || !input || !variantInput || !data) return;

    let variants;
    try {
      variants = JSON.parse(data.textContent);
    } catch {
      return;
    }
    if (!Array.isArray(variants)) return;

    const tiers = Array.from(root.querySelectorAll('[data-lmb-discount-quantity]'));
    const one = root.querySelector('[data-lmb-discount-one]');
    const other = root.querySelector('[data-lmb-discount-other]');
    const count = root.querySelector('[data-lmb-discount-count]');
    const amount = root.querySelector('[data-lmb-total]');
    const originalAmount = root.querySelector('[data-lmb-total-original]');
    const saving = root.querySelector('[data-lmb-total-savings]');
    const savingAmount = root.querySelector('[data-lmb-total-saving-amount]');
    const totalPanel = root.querySelector('[data-lmb-total-panel]');
    const totalLabel = root.querySelector('[data-lmb-total-label]');
    const totalScope = root.querySelector('[data-lmb-total-scope]');
    const syncError = root.querySelector('[data-lmb-discount-sync-error]');
    const enabled = root.dataset.lmbDiscountEnabled === 'true';
    const ownedCodes = (root.dataset.lmbDiscountCodes || '').split(',').map((code) => code.trim()).filter(Boolean);
    let discountReady = false;
    let eligibleIds = [];
    try { eligibleIds = JSON.parse(root.querySelector('[data-lmb-discount-eligible-products]')?.textContent || '[]').map(String); } catch {}
    let eligibleCartQuantity = Math.max(0, Number(root.dataset.lmbCartEligibleQuantity) || 0);
    let cartVariantQuantities = null;
    const cartContext = root.querySelector('[data-lmb-discount-cart-context]');
    let checkoutPending = false;
    const replayedCheckout = new WeakSet();
    const acceleratedButtons = form.querySelector('.product-add-to-cart__additional-buttons');
    const acceleratedWasHidden = acceleratedButtons ? acceleratedButtons.hidden : false;
    let discountTask = null;
    let discountAgain = false;
    let eventBus = null;
    const formatter = (() => {
      try { return new Intl.NumberFormat(root.dataset.lmbLocale || document.documentElement.lang || 'de', { style: 'currency', currency: root.dataset.lmbCurrency || 'EUR' }); }
      catch { return null; }
    })();
    const money = (cents) => formatter ? formatter.format(cents / 100) : (window.Shopify && typeof window.Shopify.formatMoney === 'function' ? window.Shopify.formatMoney(cents) : (cents / 100).toFixed(2) + ' ' + (root.dataset.lmbCurrency || 'EUR'));
    let destroyed = false;
    let pending = false;
    let quantityInteracted = false;

    const selectedVariant = () => {
      const options = ['option1', 'option2', 'option3'].map((name) => {
        const control = Array.from(form.querySelectorAll(`[name="${name}"]`)).find((item) => item.type !== 'radio' || item.checked);
        return control ? control.value : null;
      });
      if (options.some((value) => value !== null)) {
        return variants.find((variant) => options.every((value, index) => value === null || variant.options[index] === value));
      }
      return variants.find((variant) => String(variant.id) === String(variantInput.value));
    };

    const cartQuantity = (variant) => {
      if (cartVariantQuantities) return cartVariantQuantities.get(String(variant.id)) || 0;
      const cart = window.themeCore && window.themeCore.cartObject;
      if (cart && Array.isArray(cart.items)) {
        return cart.items.reduce((total, item) => String(item.variant_id) === String(variant.id) ? total + Number(item.quantity || 0) : total, 0);
      }
      return Number(variant.cart_quantity || 0);
    };

    const limits = (variant) => {
      const minimum = Math.max(1, Number(variant && variant.minimum) || 1);
      const increment = Math.max(1, Number(variant && variant.increment) || 1);
      let maximum = variant && variant.maximum !== null && Number(variant.maximum) > 0 ? Number(variant.maximum) : Infinity;
      if (variant && variant.stock_limit !== null) {
        maximum = Math.min(maximum, Math.max(0, Number(variant.stock_limit) - cartQuantity(variant)));
      }
      return { minimum, increment, maximum };
    };

    const validQuantity = (quantity, variant, rule) => {
      const nativeMinimum = Math.max(1, Number(input.getAttribute('min')) || 1);
      const nativeIncrement = Math.max(1, Number(input.getAttribute('step')) || 1);
      const nativeMaximum = Number(input.getAttribute('max')) > 0 ? Number(input.getAttribute('max')) : Infinity;
      return Boolean(variant && variant.available && Number.isInteger(quantity) && quantity >= rule.minimum && quantity >= nativeMinimum && quantity <= rule.maximum && quantity <= nativeMaximum && quantity % rule.increment === 0 && (quantity - nativeMinimum) % nativeIncrement === 0);
    };

    const sync = () => {
      if (destroyed) return;
      const variant = selectedVariant();
      const rule = limits(variant);
      const quantity = Number(input.value);
      const projectedQuantity = quantity + eligibleCartQuantity;
      if (cartContext) cartContext.hidden = eligibleCartQuantity === 0;
      root.dataset.lmbDiscountReady = String(discountReady);
      root.dataset.lmbDiscountCartQuantity = String(eligibleCartQuantity);
      if (acceleratedButtons) {
        const discountApplies = enabled && projectedQuantity >= 2;
        acceleratedButtons.hidden = acceleratedWasHidden || discountApplies;
        acceleratedButtons.toggleAttribute('data-lmb-discount-payment-hidden', discountApplies);
      }
      let active = null;
      let selected = null;
      if (validQuantity(quantity, variant, rule)) {
        tiers.forEach((tier) => {
          if (projectedQuantity >= Number(tier.dataset.lmbDiscountQuantity)) active = tier;
          if (quantityInteracted && quantity >= Number(tier.dataset.lmbDiscountQuantity) && validQuantity(Number(tier.dataset.lmbDiscountQuantity), variant, rule)) selected = tier;
        });
      }
      const valid = validQuantity(quantity, variant, rule) && Number.isSafeInteger(variant.price) && variant.price >= 0;
      const hasSelection = Boolean(selected);
      if (totalPanel) totalPanel.hidden = !valid;
      if (totalLabel) totalLabel.hidden = !hasSelection;
      if (totalScope) totalScope.hidden = !hasSelection;
      if (valid && amount) {
        const original = hasSelection ? variant.price * quantity : 0;
        const percent = hasSelection && active && discountReady ? Number(active.dataset.lmbDiscountPercent) : 0;
        const reduction = root.dataset.lmbDiscountRounding === 'unit-floor' ? Math.floor(variant.price * percent / 100) * quantity : root.dataset.lmbDiscountRounding === 'unit' ? Math.round(variant.price * percent / 100) * quantity : Math.round(original * percent / 100);
        amount.textContent = money(original - reduction);
        if (originalAmount) { originalAmount.hidden = reduction === 0; originalAmount.textContent = money(original); }
        if (saving) saving.hidden = reduction === 0;
        if (savingAmount) savingAmount.textContent = money(reduction);
      }
      tiers.forEach((tier) => {
        const available = validQuantity(Number(tier.dataset.lmbDiscountQuantity), variant, rule);
        tier.disabled = !available;
        tier.setAttribute('aria-pressed', String(available && tier === selected));
        const unavailable = tier.querySelector('[data-lmb-discount-unavailable]');
        if (unavailable) unavailable.hidden = available;
      });
      if (Number.isInteger(quantity) && quantity > 0) {
        if (one) one.hidden = quantity !== 1;
        if (other) other.hidden = quantity === 1;
        if (count) count.textContent = String(quantity);
      }
    };

    const scheduleSync = () => {
      if (pending || destroyed) return;
      pending = true;
      queueMicrotask(() => {
        pending = false;
        sync();
      });
    };

    const choose = (event) => {
      const tier = event.target.closest('[data-lmb-discount-quantity]');
      if (!tier || !root.contains(tier) || tier.disabled) return;
      event.preventDefault();
      const variant = selectedVariant();
      const quantity = Number(tier.dataset.lmbDiscountQuantity);
      if (!validQuantity(quantity, variant, limits(variant))) {
        sync();
        return;
      }
      quantityInteracted = true;
      input.value = String(quantity);
      input.dispatchEvent(new Event('change', { bubbles: true }));
      scheduleSync();
    };

    const updateCart = (cart, ready) => {
      if (destroyed) return;
      if (cart && Array.isArray(cart.items)) {
        eligibleCartQuantity = cart.items.reduce((total, item) => eligibleIds.includes(String(item.product_id)) ? total + Number(item.quantity || 0) : total, 0);
        cartVariantQuantities = new Map();
        cart.items.forEach((item) => { const id = String(item.variant_id); cartVariantQuantities.set(id, (cartVariantQuantities.get(id) || 0) + Number(item.quantity || 0)); });
      }
      discountReady = ready;
      if (syncError) syncError.hidden = ready;
      sync();
    };
    const refreshDiscounts = () => {
      if (!enabled || !ownedCodes.length || destroyed) return Promise.resolve(false);
      if (cartManager) return cartManager.sync().then((ready) => { updateCart(cartManager.getCart(), ready); return ready; });
      if (discountTask) { discountAgain = true; return discountTask; }
      const route = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
      const endpoint = (route.endsWith('/') ? route : route + '/') + 'cart';
      discountTask = (async () => {
        const response = await fetch(endpoint + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('cart');
        const cart = await response.json();
        if (!Array.isArray(cart.items)) throw new Error('items');
        if (!destroyed) {
          eligibleCartQuantity = cart.items.reduce((total, item) => eligibleIds.includes(String(item.product_id)) ? total + Number(item.quantity || 0) : total, 0);
          cartVariantQuantities = new Map();
          cart.items.forEach((item) => { const id = String(item.variant_id); cartVariantQuantities.set(id, (cartVariantQuantities.get(id) || 0) + Number(item.quantity || 0)); });
        }
        if (!Array.isArray(cart.discount_codes)) throw new Error('codes');
        const codes = cart.discount_codes.map((entry) => entry.code).filter((code) => typeof code === 'string' && code.length);
        const expected = Array.from(new Set([...codes, ...ownedCodes].map((code) => code.toUpperCase())));
        if (ownedCodes.some((code) => !codes.some((existing) => existing.toUpperCase() === code.toUpperCase()))) {
          const update = await fetch(endpoint + '/update.js', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: expected.join(',') }) });
          if (!update.ok) throw new Error('discount');
          const updated = await update.json();
          if (!Array.isArray(updated.discount_codes) || ownedCodes.some((code) => !updated.discount_codes.some((entry) => String(entry.code).toUpperCase() === code.toUpperCase()))) throw new Error('discount');
        }
        if (!destroyed) { discountReady = true; if (syncError) syncError.hidden = true; sync(); }
        return !destroyed;
      })().catch(() => {
        if (!destroyed) { discountReady = false; if (syncError) syncError.hidden = false; sync(); }
        return false;
      }).finally(() => {
        discountTask = null;
        if (discountAgain && !destroyed) { discountAgain = false; refreshDiscounts(); }
      });
      return discountTask;
    };
    const checkoutClick = async (event) => {
      if (cartManager || !enabled || !ownedCodes.length || destroyed || event.defaultPrevented || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const target = event.target.closest('a[href], button[name="checkout"], input[name="checkout"]');
      if (!target || replayedCheckout.has(target)) return;
      if (target.tagName === 'A') {
        let url;
        try { url = new URL(target.href, window.location.href); } catch { return; }
        if (url.origin !== window.location.origin || !/\/checkout(?:\/|$)/.test(url.pathname)) return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (checkoutPending) return;
      checkoutPending = true;
      target.setAttribute('aria-busy', 'true');
      const ready = await refreshDiscounts();
      checkoutPending = false;
      target.removeAttribute('aria-busy');
      if (ready && !destroyed) {
        replayedCheckout.add(target);
        target.click();
        replayedCheckout.delete(target);
      }
    };
    const checkoutSubmit = async (event) => {
      if (cartManager || !enabled || !ownedCodes.length || destroyed || event.defaultPrevented) return;
      const target = event.target;
      const submitter = event.submitter;
      if (!target || target.tagName !== 'FORM' || replayedCheckout.has(target)) return;
      const action = target.getAttribute('action') || '';
      if (!(submitter && submitter.name === 'checkout') && !/\/checkout(?:\/|$)/.test(action)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (checkoutPending) return;
      checkoutPending = true;
      if (submitter) submitter.setAttribute('aria-busy', 'true');
      const ready = await refreshDiscounts();
      checkoutPending = false;
      if (submitter) submitter.removeAttribute('aria-busy');
      if (ready && !destroyed) {
        replayedCheckout.add(target);
        target.requestSubmit(submitter || undefined);
        replayedCheckout.delete(target);
      }
    };
    const cartUpdated = () => { scheduleSync(); if (!cartManager) refreshDiscounts(); };
    const formChanged = (event) => {
      if (event.target === input) quantityInteracted = true;
      scheduleSync();
    };
    const attachCart = () => {
      const bus = window.themeCore && window.themeCore.EventBus;
      if (bus && typeof bus.listen === 'function' && !eventBus) { eventBus = bus; bus.listen('cart:updated', cartUpdated); }
      refreshDiscounts();
    };
    document.addEventListener('click', checkoutClick, true);
    document.addEventListener('submit', checkoutSubmit, true);
    root.addEventListener('click', choose);
    form.addEventListener('input', formChanged);
    form.addEventListener('change', formChanged);
    document.addEventListener('product:variant:change', scheduleSync);
    const ruleObserver = new MutationObserver(scheduleSync);
    ruleObserver.observe(input, { attributes: true, attributeFilter: ['min', 'max', 'step'] });
    instances.set(root, {
      updateCart,
      destroy() {
        destroyed = true;
        document.removeEventListener('click', checkoutClick, true);
        document.removeEventListener('submit', checkoutSubmit, true);
        root.removeEventListener('click', choose);
        form.removeEventListener('input', formChanged);
        form.removeEventListener('change', formChanged);
        document.removeEventListener('product:variant:change', scheduleSync);
        ruleObserver.disconnect();
        if (acceleratedButtons) { acceleratedButtons.hidden = acceleratedWasHidden; acceleratedButtons.removeAttribute('data-lmb-discount-payment-hidden'); }
        window.removeEventListener('load', attachCart);
        if (eventBus) eventBus.remove('cart:updated', cartUpdated);
        instances.delete(root);
      }
    });
    sync();
    attachCart();
    window.addEventListener('load', attachCart, { once: true });
  };

  const init = (root = document) => {
    if (root.matches && root.matches(cartSelector)) initialiseCart(root);
    root.querySelectorAll(cartSelector).forEach(initialiseCart);
    if (root.matches && root.matches(selector)) initialise(root);
    root.querySelectorAll(selector).forEach(initialise);
  };

  window.__lmbPdpDiscounts = { init };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, root) => {
      if (root === event.target || event.target.contains(root)) instance.destroy();
    });
  });
})();
